/**
 * English source-of-truth. All other languages must implement these keys.
 *
 * Codenames (BOOT.01, MASK.07, FIRST.RUN, etc.) and the level/chapter
 * codename fields stay English by design — they're meant to read like
 * opcodes/handles. Long-form level intros/hints stay English for now;
 * everything player-facing in the UI is translated.
 */
export const en = {
  // ── Common buttons / status ────────────────────────────────────────────────
  back: 'BACK',
  next: 'NEXT',
  retry: 'RETRY',
  replay: 'REPLAY',
  sector: 'SECTOR',
  sectors: 'SECTORS',
  levels: 'LEVELS',
  cleared: 'CLEARED',
  open: 'OPEN',
  locked: 'LOCKED',
  tba: 'TBA',
  slot_reserved: 'SLOT RESERVED',
  sfx_on: 'SFX ON',
  sfx_off: 'SFX OFF',
  reset_progress: 'RESET PROGRESS',
  wiped_reloading: 'WIPED, RELOADING…',
  got_it: '› GOT IT ‹',

  // ── Boot screen ────────────────────────────────────────────────────────────
  boot_eyebrow: 'CASCADE PROTOCOL  //  BUILD',
  boot_tagline: 'ONE TAP. ONE CASCADE. NO REDO.',
  boot_subtitle: '信号崩壊  ·  CASCADE LOGIC',
  boot_cta: 'Tap to play',
  boot_region: 'REGION ▣ /7',
  boot_top_left: 'HAYTERWAVE//SYSCORE',
  boot_top_right_status: '0xC7A5 // ONLINE',

  // ── Menu / sector select ───────────────────────────────────────────────────
  menu_header_subtitle: 'NØDUS / SECTOR.SELECT',
  menu_engine_label: 'signal collapse engine',
  menu_signals_down: 'signals down',
  menu_tap_chapter: 'TAP A CHAPTER TO ENTER',
  menu_language_label: 'LANG',

  // ── Chapter scene ──────────────────────────────────────────────────────────
  chapter_signals_down: 'signals down',
  chapter_tba_label: '0xTBA',
  chapter_coda_name: 'CODA',
  chapter_coda_tagline: 'Nothing new. Everything combined.',

  // ── Game HUD ───────────────────────────────────────────────────────────────
  hud_tap_to_breach: '▾  tap any node to breach  ▾',
  hud_tap_to_skip: 'tap to skip  ▸▸',
  hud_drag_zoom_help: 'drag · pinch zoom · 2-tap recenter',
  hud_required: 'REQUIRED',
  hud_core_online: 'CORE ONLINE',
  hud_core_down: 'CORE DOWN',

  // ── Tutorial finger panel ──────────────────────────────────────────────────
  tut_tap_any_node: 'TAP ANY NODE',
  tut_they_all_work: 'they all work on this one',

  // ── Mission brief (first-ever-launch card) ─────────────────────────────────
  brief_eyebrow: '◂ MISSION BRIEF ▸',
  brief_title: 'ONE TAP.\nONE CASCADE.',
  brief_line_1: '› You get exactly ONE tap.',
  brief_line_2: '› It triggers a chain reaction through the network.',
  brief_line_3: '› Collapse every REQUIRED (cyan diamond) to win.',
  brief_line_4: '› Never let a pulse touch PROTECTED (red).',
  brief_begin: '› BEGIN BRIEFING ‹',

  // ── Result scene ───────────────────────────────────────────────────────────
  result_win_headline: 'SYSTEM CRASHED',
  result_fail_headline: 'CASCADE FAILED',
  result_first_clear: '› FIRST CLEAR ‹',
  result_try_label: '› TRY',
  result_cleared_suffix: 'CLEARED',
  result_aborted_suffix: 'RUN ABORTED',
  /** Template: "{ticks} ticks. {nodes} nodes down." */
  result_win_body_template: '{ticks} ticks. {nodes} nodes down.',

  // ── Failure explanations + suggested fixes ────────────────────────────────
  fail_protected_overload: 'Protected node overloaded. The cascade hit something it shouldn\'t have.',
  fail_forbidden_trigger: 'Forbidden line carried a signal. Tripped a hard alarm.',
  fail_required_not_reached: 'A required node never received a pulse. The chain didn\'t reach it.',
  fail_required_under_threshold: 'A required node didn\'t get enough pulses. Decay killed the first before the second landed.',
  fail_core_not_collapsed: 'The core survived. The cascade dissipated before reaching it.',
  fail_core_under_threshold: 'The core got pulses but not enough, they didn\'t converge in time.',
  fail_core_shielded: 'Core pulses blocked. A shield was still online.',
  fail_chain_died: 'Chain died before reaching anything.',
  fix_protected_overload: 'Pick a starter whose cascade never reaches the red hazard. Trace each starter\'s downstream wires first.',
  fix_forbidden_trigger: 'Avoid starters that fire INTO a red dashed wire. The alarm trips the moment a pulse touches it.',
  fix_required_not_reached: 'Your starter doesn\'t connect to that node. Check the arrows, directional wires only flow one way.',
  fix_required_under_threshold: 'Find a route that delivers two pulses to that node, a splitter or two converging paths usually does it.',
  fix_core_not_collapsed: 'You need more convergence into the core. Splitters, capacitors and relays exist to feed it.',
  fix_core_under_threshold: 'Pulses are arriving too slowly. Use splitters to stack hits, or land all pulses on the same tick.',
  fix_core_shielded: 'Drop every shield before any kill-shot lands. A delay wire arriving late is a clean way to do this.',
  fix_chain_died: 'Your starter only connects to one node. Pick one in the middle of the web.',

  // ── Popup eyebrows ─────────────────────────────────────────────────────────
  popup_new_node: '◂ NEW NODE TYPE ▸',
  popup_new_wire: '◂ NEW WIRE TYPE ▸',

  // ── Node intro popup copy (titles, taglines, bullets, optional notes) ─────
  node_normal_title: 'NODE',
  node_normal_tagline: 'The base unit. Tap it to start a cascade.',
  node_normal_b1: 'Collapses on the first pulse that arrives.',
  node_normal_b2: 'Fires a pulse on every open outgoing wire.',
  node_normal_b3: 'No threshold, one hit and it\'s gone.',

  node_required_title: 'REQUIRED',
  node_required_tagline: 'You must collapse every REQUIRED to win.',
  node_required_b1: 'Cyan diamond. Pulses faintly at idle so you can spot it.',
  node_required_b2: 'Some need 2+ pulses arriving in the same window.',
  node_required_b3: 'Miss even one and the run fails.',
  node_required_note: 'A small ring of pips around the node shows its charge progress.',

  node_protected_title: 'PROTECTED',
  node_protected_tagline: 'Touch it and the whole run dies.',
  node_protected_b1: 'Red hazard cross. Marked with corner brackets.',
  node_protected_b2: 'Any pulse landing on it triggers instant FAIL.',
  node_protected_b3: 'Plan a route that never feeds into it.',

  node_relay_title: 'RELAY',
  node_relay_tagline: 'Opens locked wires when it collapses.',
  node_relay_b1: 'Gold chevron. Targets are drawn as DASHED LOCKED wires.',
  node_relay_b2: 'Once the relay falls, its locked wires light up and conduct.',
  node_relay_b3: 'Useful for revealing a path into a guarded node.',

  node_shield_title: 'SHIELD',
  node_shield_tagline: 'A bodyguard. Absorbs hits for another node.',
  node_shield_b1: 'Green hexagon ring around the protected node.',
  node_shield_b2: 'A direct pulse on a shielded node does NOTHING.',
  node_shield_b3: 'Collapse the shield first, then deliver the kill shot.',
  node_shield_note: 'DELAY wires (dashed gold) are perfect for timing a post-shield strike.',

  node_splitter_title: 'SPLITTER',
  node_splitter_tagline: 'Multiplies pulses across all its outputs.',
  node_splitter_b1: 'Magenta fork. One pulse in → N pulses out per output.',
  node_splitter_b2: 'The X marker shows its split factor.',
  node_splitter_b3: 'Supplies the multiple hits a high-threshold CORE needs.',

  node_capacitor_title: 'CAPACITOR',
  node_capacitor_tagline: 'Stores a pulse, then re-emits, a buffer.',
  node_capacitor_b1: 'Yellow capacitor symbol. Forwards a pulse 3 ticks later.',
  node_capacitor_b2: 'Treat it as a built-in DELAY in the route.',
  node_capacitor_b3: 'Lets a later pulse arrive AFTER a shield has dropped.',

  node_core_title: 'CORE',
  node_core_tagline: 'The objective. High threshold, fast decay.',
  node_core_b1: 'Purple reactor. Needs many converging pulses to fall.',
  node_core_b2: 'Charge DECAYS, pulses must arrive close together.',
  node_core_b3: 'Often gated by shields. Drop them, then flood the core.',
  node_core_note: 'A level with a CORE is won by collapsing it. Required nodes (if any) still count.',

  // ── Wire intro popup copy ──────────────────────────────────────────────────
  wire_directional_title: 'DIRECTIONAL WIRE',
  wire_directional_tagline: 'One-way conductor. Cyan with an arrowhead.',
  wire_directional_b1: 'Pulse only travels in the direction of the arrow.',
  wire_directional_b2: 'Tap on the wrong side of the arrow and nothing flows.',
  wire_directional_b3: 'Trace the arrows from your starter all the way down.',

  wire_delay_title: 'DELAY WIRE',
  wire_delay_tagline: 'Dashed gold. The pulse arrives 3 ticks later.',
  wire_delay_b1: 'Useful for timing, land a pulse AFTER a shield drops.',
  wire_delay_b2: 'Or for stacking two pulses on a high-threshold node.',
  wire_delay_b3: 'Get the timing wrong and the early pulse decays first.',

  wire_locked_title: 'LOCKED WIRE',
  wire_locked_tagline: 'Dashed grey + padlock. Closed until something opens it.',
  wire_locked_b1: 'A RELAY node controls each locked wire.',
  wire_locked_b2: 'When the relay collapses, its locked wires snap open.',
  wire_locked_b3: 'Don\'t waste a tap trying to feed a locked wire directly.',

  wire_forbidden_title: 'FORBIDDEN WIRE',
  wire_forbidden_tagline: 'Dashed RED with a cross. Touch it = instant fail.',
  wire_forbidden_b1: 'Any pulse, even a transit pulse, trips the alarm.',
  wire_forbidden_b2: 'Watch for these on every starter\'s outgoing wires.',
  wire_forbidden_b3: 'Pick a starter whose route NEVER touches red.',

  wire_burn_title: 'BURN WIRE',
  wire_burn_tagline: 'Brass-orange. One-shot conductor, burns out after a pulse.',
  wire_burn_b1: 'Carries exactly ONE pulse, then dies forever.',
  wire_burn_b2: 'Plan its single use carefully, there is no second chance.',
  wire_burn_b3: 'Once burned, no further cascade can route through it.',

  // ── Callouts during cascade ────────────────────────────────────────────────
  callout_required_crashed: 'REQUIRED CRASHED',
  callout_core_breached: 'CORE BREACHED',
  callout_shield_dropped: 'SHIELD DROPPED',
  callout_relay_opened: 'RELAY OPENED',
  callout_protected_hit: 'PROTECTED HIT',

  // ── Beta: settings ─────────────────────────────────────────
  menu_settings: 'SETTINGS',
  settings_title: 'SETTINGS',
  settings_sound: 'Sound',
  settings_haptics: 'Haptics',
  settings_haptics_note: 'device only',
  settings_on: 'ON',
  settings_off: 'OFF',
  settings_replay_tutorials: 'Replay tutorials',
  settings_reset_progress: 'Reset all progress',
  settings_reset_confirm: 'TAP AGAIN TO WIPE',
  settings_done: 'DONE',

  // ── Beta: achievements / share ─────────────────────────────
  menu_achievements: 'AWARDS',
  ach_title: 'ACHIEVEMENTS',
  ach_new: 'ACHIEVEMENT UNLOCKED',
  ach_locked: 'LOCKED',
  share_button: 'SHARE',
  share_copied: 'Copied to clipboard',

  // ── Beta: daily challenge ──────────────────────────────────
  daily_title: 'DAILY',
  daily_tagline: 'One fresh puzzle every day. Keep the streak alive.',
  daily_new: 'NEW TODAY',
  daily_keep: 'KEEP IT ALIVE',
  menu_play_first: 'LEVEL 1 FIRST',
  daily_done: 'SOLVED ✓',
  daily_solved: 'DAILY SOLVED',
  daily_streak: 'STREAK',

  // ── Beta: ads / lives / hints / stats ───────────────────────
  ad_label: 'ADVERTISEMENT',
  ad_house_tagline: 'One tap. One breach. Infinite levels.',
  ad_rewarded_watching: 'Watch to earn your reward',
  ad_interstitial_watching: 'Your run continues in a moment',
  ad_rewarded_done: 'Reward unlocked',
  ad_interstitial_done: 'Thanks for playing',
  ad_reward_in: 'Reward in {n}s',
  ad_claim: 'CLAIM',
  ad_continue: 'CONTINUE',
  lives_label: 'LIVES',
  run_over_headline: 'OUT OF LIVES',
  run_over_body: 'Your run is on the line. Watch an ad for a life, or bank this run.',
  watch_for_life: 'AD  +1 LIFE',
  bank_run: 'BANK RUN',
  life_lost: '-1 LIFE',
  hint_button: 'HINT',
  hint_confirm_title: 'WATCH AD FOR A HINT?',
  hint_confirm_body: 'Rules out one breach point that does NOT solve this level. It never shows you the answer.',
  hint_yes: 'WATCH AD',
  hint_removed: 'One wrong breach point ruled out',
  hint_none_left: 'Nothing left to rule out',
  no_thanks: 'NO THANKS',
  stats_title: 'STATISTICS',
  menu_stats: 'STATS',
  stats_levels_completed: 'Levels completed',
  stats_endless_best: 'Endless best',
  stats_best_streak: 'Best streak',
  stats_fail_rate: 'Fail rate',
  stats_wins: 'Wins',
  stats_fails: 'Fails',
  stats_hints_used: 'Hints used',
  stats_ads_watched: 'Ads watched',
  stats_close: 'CLOSE',
  // ── Added with the redesign ──
  result_map_tapped: 'Your tap',
  result_map_broke: 'Failed here',
  result_map_answer: 'Works',
  result_show_answer: 'Show the answer',
  menu_endless_line: 'Generated levels with three lives. The deeper you go, the harder it bites.',
  menu_best: 'Best LV {n}',
  menu_best_streak: 'Best LV {n}  ·  streak ×{s}',
  menu_resume: 'Resume LV {n}',
  menu_start: 'Start LV 1',
  result_run_total: 'Run total {n}',
  result_streak: 'Streak ×{n}',
  result_streak_wins: '{n} wins in a row',
  legend_title: 'Legend',
  legend_nodes: 'NODES',
  legend_wires: 'WIRES',
  legend_close: 'Tap anywhere to close',
  settings_sync_now: 'Sync now ({who})',
  settings_sign_in: 'Sign in to sync',
  settings_sign_out: 'Sign out',

};

export type StringKey = keyof typeof en;
export type LangStrings = Record<StringKey, string>;
